import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowLeft,
  MapPin,
  Clock,
  ShieldCheck,
  CheckCircle2,
  PhoneCall,
  PlayCircle,
  Sparkles,
  Users,
  Gift,
} from "lucide-react";
import { CORSI, IMAGES, BONUSES, FAQS, TESTIMONIALS, findCourse } from "../mock";
import Navbar from "./Navbar";
import Footer from "./Footer";
import Countdown from "./Countdown";
import WhatsAppFloat from "./WhatsAppFloat";
import Seo from "./Seo";
import CourseScheduleCard from "./CourseScheduleCard";
import DownloadProgramBox from "./DownloadProgramBox";
import { getSchedule } from "../data/schedule";
import { trackCourseLead, withTrackingParams } from "../lib/analytics";

// Extra content per course (long description, what you learn, target)
const COURSE_EXTRAS = {
  "controlla-la-tua-comunicazione-digitale": {
    longDesc:
      "Un percorso in aula pensato per chi vuole finalmente smettere di inseguire tecniche isolate e costruire un vero e proprio sistema di acquisizione clienti online. Il Metodo My Digital Action ti guiderà passo passo, dal messaggio differenziante al funnel completo.",
    audience: [
      "Imprenditori e titolari di attività locali",
      "Professionisti che vogliono attrarre clienti online",
      "Consulenti che vogliono strutturare la propria offerta",
    ],
    learn: [
      "Progettare un ecosistema digitale concreto e sostenibile",
      "Costruire un messaggio unico che ti posiziona nel mercato",
      "Creare contenuti che attraggono e vendono",
      "Guidare i tuoi contatti in un percorso automatizzato",
      "Trasformare i contatti in clienti reali con strategia replicabile",
      "Portare traffico qualificato al tuo sistema, online e offline",
    ],
  },
  "social-media-marketing": {
    longDesc:
      "Il corso per chi vuole gestire i social con metodo, senza dispersioni. Impari a costruire una strategia efficace, contenuti che funzionano e un piccolo funnel pubblicitario Meta base per attrarre clienti reali.",
    audience: [
      "Chi gestisce i social \"quando riesce\"",
      "Titolari di attività locali che vogliono più visibilità",
      "Chi vuole imparare le basi di Facebook & Instagram Ads",
    ],
    learn: [
      "Cos'è davvero il Social Media Marketing (e cosa non è)",
      "Analisi del consumatore e strategia digitale",
      "Contenuti creativi e visivi che convertono",
      "Creare un funnel di marketing digitale efficace",
      "Meta ADS Base — Facebook & Instagram",
    ],
  },
  "facebook-advanced": {
    longDesc:
      "Il corso avanzato per chi vuole scalare con Meta Ads. Strategie pubblicitarie basate su dati reali, targeting intelligente, struttura Power 5, budget e testing. Zero teoria, solo playbook operativi.",
    audience: [
      "Chi ha già campagne attive e vuole ottimizzarle",
      "Media buyer interni delle aziende",
      "Imprenditori che vogliono capire davvero il ROI degli ads",
    ],
    learn: [
      "Introduzione a Meta Ads 2.0",
      "Fondamenti del Business Manager",
      "Algoritmo e targeting intelligente",
      "Struttura Power 5 e automazioni",
      "Creatività e copywriting che convertono",
      "Budget, testing e scaling",
      "Lettura dei dati e analisi",
      "Strategie specifiche per settore + casi studio",
    ],
  },
  "customer-loyalty": {
    longDesc:
      "Il corso pratico su come automatizzare la fidelizzazione clienti e massimizzare il loro valore nel tempo. Database marketing, CRM, programmi fedeltà e strategia One-Time Offer per aumentare i margini senza spendere di più in acquisizione.",
    audience: [
      "Attività con database clienti sottoutilizzato",
      "E-commerce che vogliono aumentare LTV",
      "Chi vuole strutturare programmi fedeltà pratici",
    ],
    learn: [
      "Fondamenti del Database Marketing e CRM",
      "Programmi fedeltà e automazioni",
      "Workflow di fidelizzazione con strategia OTO",
      "Integrazione offline e online",
      "Roadmap personalizzata di applicazione",
      "Laboratorio pratico sul tuo caso",
    ],
  },
  "agenti-ai": {
    longDesc:
      "Il corso più attuale del programma: costruire e integrare Agenti AI nel tuo business per contenuti, customer care, vendite e upsell. Un vantaggio competitivo che poche PMI stanno cogliendo — noi ti mostriamo come.",
    audience: [
      "Imprenditori curiosi dell'AI ma senza tempo per capirla",
      "Chi vuole automatizzare marketing, customer care e vendite",
      "Professionisti che vogliono un vantaggio competitivo",
    ],
    learn: [
      "Introduzione agli Agenti AI (concetti chiave, no teoria)",
      "AI per Marketing e Content Creation",
      "AI per Customer Care 24/7",
      "AI per Vendite e Up-Sell",
      "Laboratorio pratico + caso studio reale",
      "Normative, etica e futuro degli Agenti AI",
    ],
  },
};

export default function CoursePage({ onOpenBooking, onOpenCheckout }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const course = findCourse(slug);
  const extra = COURSE_EXTRAS[course?.slug];
  const canonicalSlug = course ? (course.urlSlug || course.slug) : slug;
  const schedule = getSchedule(course?.slug);
  const [videoPlaying, setVideoPlaying] = useState(false);

  useEffect(() => { setVideoPlaying(false); }, [slug]);

  // Redirect legacy slug (e.g. /corso/facebook-advanced) to the canonical URL.
  useEffect(() => {
    if (course && course.urlSlug && slug !== course.urlSlug) {
      navigate(withTrackingParams(`/corso/${course.urlSlug}`), { replace: true });
    }
  }, [course, slug, navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [course, slug]);

  if (!course || !extra) {
    return (
      <div className="App grain min-h-screen">
        <Navbar />
        <div className="container-max py-40 text-center">
          <div className="font-mono-label text-white/40 text-xs">404</div>
          <h1 className="font-display text-5xl text-white mt-4">Corso non trovato</h1>
          <p className="text-white/60 mt-4">Il corso che cerchi non esiste o è stato spostato.</p>
          <Link to="/" className="btn-primary mt-8 inline-flex">
            <ArrowLeft size={16} /> Torna alla home
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="App grain">
      <Seo
        title={`${course.title} — Corso in presenza a Pisa | MyDigitalAction`}
        description={`${course.subtitle}. ${course.goal} Corso in presenza a Pisa, garanzia soddisfatti o rimborsati.`}
        path={`/corso/${canonicalSlug}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Course",
          name: course.title,
          description: `${course.subtitle}. ${course.goal}`,
          provider: {
            "@type": "Organization",
            name: "MyDigitalAction Experience",
            sameAs: "https://mydigitalaction.it",
          },
          offers: {
            "@type": "Offer",
            price: String(course.price || "260"),
            priceCurrency: "EUR",
            availability: "https://schema.org/InStock",
            url: `https://mydigitalaction.it/corso/${canonicalSlug}`,
            category: "Formazione in presenza",
          },
          hasCourseInstance: {
            "@type": "CourseInstance",
            courseMode: "onsite",
            location: {
              "@type": "Place",
              name: "Montacchiello Campus",
              address: {
                "@type": "PostalAddress",
                addressLocality: "Pisa",
                addressCountry: "IT",
              },
            },
          },
        }}
      />
      <Navbar onOpenBooking={onOpenBooking} />
      <section className="relative pt-28 lg:pt-32 pb-16 lg:pb-20 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <img
            src={IMAGES.heroBanner}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover grayscale"
            style={{ opacity: 0.25 }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/95 via-[#0a0a0a]/70 to-[#0a0a0a]" />
        </div>
        <div className="absolute inset-0 gradient-glow pointer-events-none opacity-50" />

        <div className="container-max relative z-10">
          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-8 transition-colors"
          >
            <ArrowLeft size={14} /> Tutti i corsi
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-black/40 backdrop-blur-md px-3 py-1.5 text-[11px] tracking-widest uppercase text-white/80">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#3b5bff" }} />
                  Corso {course.n} / 05
                </span>
                <Countdown />
              </div>

              <span className="section-label">Corso singolo in presenza</span>

              <h1 className="font-display text-[36px] sm:text-[46px] lg:text-[60px] leading-[1.05] font-bold text-white mt-6">
                {course.title}
              </h1>

              <p className="mt-4 text-white/70 text-base lg:text-lg max-w-2xl leading-relaxed">
                {course.subtitle}
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-white/80 text-xs lg:text-sm">
                <span className="inline-flex items-center gap-2">
                  <Clock size={14} className="text-white/50" /> {course.hours}
                </span>
                <span className="text-white/20">|</span>
                <span className="inline-flex items-center gap-2">
                  <MapPin size={14} className="text-white/50" /> Montacchiello Campus, Pisa
                </span>
                <span className="text-white/20">|</span>
                <span className="inline-flex items-center gap-2">
                  <ShieldCheck size={14} className="text-white/50" /> Garanzia zero rischi
                </span>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a href="#acquista" className="btn-primary" onClick={() => trackCourseLead({ courseName: course.title })}>
                  Iscriviti — €{course.price} <ArrowUpRight size={18} />
                </a>
                <button
                  type="button"
                  onClick={onOpenBooking}
                  className="inline-flex items-center gap-3 bg-black/40 backdrop-blur-sm border border-white/20 hover:border-white/50 rounded-full py-3 px-5 text-white transition-colors"
                >
                  <PhoneCall size={16} />
                  <span className="flex flex-col items-start leading-tight">
                    <span className="text-sm font-medium">Parla con un ns consulente</span>
                    <span className="text-[10px] text-white/50 tracking-wide uppercase">
                      call gratuita e non impegnativa
                    </span>
                  </span>
                </button>
              </div>
            </div>

            {/* Video hero */}
            <div className="lg:col-span-5">
              {course.wistiaId ? (
                <div className="relative rounded-2xl overflow-hidden border border-white/10 aspect-video bg-[#141414]" data-testid="course-hero-video">
                  {course.videoCover && !videoPlaying ? (
                    <button
                      type="button"
                      onClick={() => setVideoPlaying(true)}
                      className="absolute inset-0 w-full h-full group cursor-pointer"
                      data-testid="course-video-cover"
                      aria-label="Riproduci il video di presentazione"
                    >
                      <img
                        src={course.videoCover}
                        alt={`Video presentazione — ${course.title}`}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      <span className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-black/0 transition-colors">
                        <span className="w-16 h-16 rounded-full bg-black/50 backdrop-blur-sm border border-white/30 flex items-center justify-center group-hover:scale-110 transition-transform">
                          <PlayCircle size={32} className="text-white" />
                        </span>
                      </span>
                    </button>
                  ) : (
                    <iframe
                      src={`https://fast.wistia.net/embed/iframe/${course.wistiaId}?videoFoam=true&playerColor=6b8bff${videoPlaying ? "&autoPlay=true" : ""}`}
                      title={`Video presentazione — ${course.title}`}
                      allow="autoplay; fullscreen"
                      allowFullScreen
                      className="absolute inset-0 w-full h-full"
                      frameBorder="0"
                    />
                  )}
                </div>
              ) : (
                <div className="relative rounded-2xl overflow-hidden border border-white/10 aspect-video bg-[#141414] flex items-center justify-center group cursor-pointer">
                  <img
                    src={IMAGES.training}
                    alt="Course preview"
                    className="absolute inset-0 w-full h-full object-cover grayscale opacity-30 group-hover:opacity-40 transition-opacity"
                  />
                  <div className="relative z-10 flex flex-col items-center">
                    <div className="w-16 h-16 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center mb-4 group-hover:bg-white/20 transition-colors">
                      <PlayCircle size={30} className="text-white" />
                    </div>
                    <div className="text-white text-sm font-medium">Video preview</div>
                    <div className="text-white/50 text-xs mt-1">Presto disponibile</div>
                  </div>
                </div>
              )}

              <div className="mt-4 grid grid-cols-2 gap-3">
                <div className="border border-white/10 rounded-xl p-4 bg-black/30 backdrop-blur-sm">
                  <div className="font-mono-label text-white/40 text-[10px]">Prezzo oggi</div>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="font-display text-3xl text-white">€{course.price}</span>
                    <span className="text-white/40 text-xs line-through decoration-white/30">
                      €{course.originalPrice}
                    </span>
                  </div>
                </div>
                <div className="border border-white/10 rounded-xl p-4 bg-black/30 backdrop-blur-sm">
                  <div className="font-mono-label text-white/40 text-[10px]">Sconto</div>
                  <div className="font-display text-3xl mt-1" style={{ color: "#6b8bff" }}>
                    -50%
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROSSIMA EDIZIONE — scheda-date */}
      {schedule && (
        <section className="pt-2 pb-10 lg:pb-12" data-testid="course-schedule-section">
          <div className="container-max max-w-5xl">
            <CourseScheduleCard
              schedule={schedule}
              testId="course-schedule"
              ctaLabel={`Iscriviti — €${course.price}`}
              onCta={() => {
                trackCourseLead({ courseName: course.title });
                const el = document.getElementById("acquista");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
            />
          </div>
        </section>
      )}

      {/* OBIETTIVO + LONG DESC */}
      <section className="py-16 lg:py-24 border-t border-white/5">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <span className="section-label">Obiettivo</span>
              <h2 className="font-display text-3xl lg:text-5xl leading-tight text-white mt-6">
                {course.goal}
              </h2>
            </div>
            <div className="lg:col-span-7">
              <p className="text-white/70 text-base lg:text-lg leading-relaxed">{extra.longDesc}</p>

              <div className="mt-10">
                <div className="font-mono-label text-white/40 text-[11px] mb-4 flex items-center gap-2">
                  <Users size={12} /> A chi è rivolto
                </div>
                <ul className="space-y-3">
                  {extra.audience.map((a, i) => (
                    <li key={i} className="flex items-start gap-3 text-white/85">
                      <CheckCircle2 size={18} className="text-lime flex-shrink-0 mt-0.5" style={{ color: "#d4ff3d" }} />
                      <span>{a}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COSA IMPARERAI */}
      <section className="py-16 lg:py-24 bg-[#0c0c0c] border-y border-white/5">
        <div className="container-max">
          <div className="mb-12">
            <span className="section-label">Cosa imparerai</span>
            <h2 className="font-display text-3xl lg:text-5xl text-white mt-6 max-w-3xl leading-tight">
              Alla fine di questa giornata,<br />
              <span className="italic font-light text-white/60">saprai fare tutto questo.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {extra.learn.map((l, i) => (
              <div
                key={i}
                className="bg-card border border-white/10 rounded-2xl p-5 lg:p-6 flex items-start gap-4 hover-lift"
              >
                <span className="font-mono-label text-white/30 text-xs mt-1">
                  0{i + 1}
                </span>
                <div className="flex items-start gap-3">
                  <CheckCircle2 size={18} className="text-lime flex-shrink-0 mt-0.5" style={{ color: "#d4ff3d" }} />
                  <span className="text-white/90 text-base">{l}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MODULI DEL CORSO */}
      <section className="py-16 lg:py-24">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-4">
              <span className="section-label">Programma completo</span>
              <h2 className="font-display text-3xl lg:text-5xl text-white mt-6 leading-tight">
                Moduli del corso
              </h2>
              <p className="text-white/60 mt-6 text-base leading-relaxed">
                Tutto quello che vedremo insieme in aula. Ogni modulo è pratico e ha esercitazioni immediate.
              </p>
              <div className="mt-8 border border-white/10 rounded-xl p-5 bg-card">
                <div className="font-mono-label text-white/40 text-[11px] mb-2">Durata totale</div>
                <div className="font-display text-3xl text-white">{course.hours}</div>
                <div className="text-white/50 text-xs mt-2">
                  in una giornata unica, colazione e pranzo inclusi
                </div>
              </div>
            </div>

            <div className="lg:col-span-8">
              <ul className="space-y-3">
                {course.modules.map((m, i) => (
                  <li
                    key={i}
                    className="bg-card border border-white/10 rounded-2xl p-5 lg:p-6 flex items-center gap-5 hover-lift"
                  >
                    <div className="font-display text-3xl text-white/30 min-w-[3rem]">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="flex-1">
                      <div className="text-white text-base lg:text-lg font-medium">{m}</div>
                    </div>
                    <CheckCircle2 size={18} className="text-lime flex-shrink-0" style={{ color: "#d4ff3d" }} />
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {course.slug === "social-media-marketing" && (
        <section className="pb-4 lg:pb-8" data-testid="course-program-download-section">
          <div className="container-max">
            <DownloadProgramBox slug="social-media-marketing" programName="Social Media Marketing" />
          </div>
        </section>
      )}

      {/* BONUS INCLUSI */}
      <section className="py-16 lg:py-24 bg-[#0c0c0c] border-y border-white/5">
        <div className="container-max">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <span className="section-label">5 Bonus inclusi</span>
              <h2 className="font-display text-3xl lg:text-5xl text-white mt-6 max-w-2xl leading-tight">
                Iscrivendoti oggi,<br />
                <span className="italic font-light text-white/60">ricevi anche questi 5 bonus.</span>
              </h2>
            </div>
            <div className="text-right">
              <div className="font-mono-label text-white/40 text-[11px]">Valore complessivo bonus</div>
              <div className="font-display text-3xl lg:text-4xl" style={{ color: "#d4ff3d" }}>
                €4.143+
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
            {BONUSES.map((b) => (
              <div key={b.id} className="bg-card border border-white/10 rounded-2xl p-5 hover-lift flex flex-col">
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                    <Gift size={16} className="text-white" />
                  </div>
                  <div className="font-mono-label text-white/30 text-[10px]">{b.tag}</div>
                </div>
                <div className="font-display text-lg text-white">{b.name}</div>
                <p className="text-white/60 text-xs mt-2 leading-relaxed flex-1">{b.desc}</p>
                <div className="mt-4 pt-3 border-t border-white/10">
                  <div className="font-display text-lg text-white">{b.value}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PREZZO / ACQUISTA */}
      <section id="acquista" className="py-16 lg:py-24 relative overflow-hidden">
        <div className="absolute inset-0 gradient-bottom pointer-events-none" />
        <div className="container-max relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-6">
              <span className="section-label">Investimento</span>
              <h2 className="font-display text-3xl lg:text-5xl text-white mt-6 leading-tight">
                Iscriviti oggi<br />
                <span className="italic font-light text-white/60">a un prezzo speciale di lancio.</span>
              </h2>
              <p className="text-white/70 text-base lg:text-lg mt-6 max-w-lg leading-relaxed">
                Un unico corso completo, con tutti i 5 bonus inclusi. Posti limitati per ogni sessione.
                Sconto del 50% valido solo per la promo Early Bird.
              </p>

              <div className="mt-8 space-y-2 text-white/75 text-sm">
                <div className="flex items-center gap-3">
                  <CheckCircle2 size={16} className="text-lime" style={{ color: "#d4ff3d" }} />
                  <span>{course.hours} in aula, materiali e pranzo inclusi</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 size={16} className="text-lime" style={{ color: "#d4ff3d" }} />
                  <span>Tutti e 5 i Bonus (valore oltre €4.143)</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 size={16} className="text-lime" style={{ color: "#d4ff3d" }} />
                  <span>Garanzia soddisfatto o rimborsato entro le ore 13</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircle2 size={16} className="text-lime" style={{ color: "#d4ff3d" }} />
                  <span>3 rate senza interessi con PayPal</span>
                </div>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="bg-card border border-white/10 rounded-3xl p-8 lg:p-10 relative overflow-hidden">
                <div
                  className="absolute -top-24 -right-24 w-64 h-64 rounded-full opacity-[0.06] blur-3xl"
                  style={{ background: "#d4ff3d" }}
                />
                <div className="font-mono-label text-xs" style={{ color: "#d4ff3d" }}>
                  Early Bird — Sconto 50%
                </div>
                <div className="mt-4 flex items-baseline gap-3">
                  <span className="text-white/40 text-xl line-through decoration-white/30">
                    €{course.originalPrice}
                  </span>
                </div>
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="font-display text-white text-6xl lg:text-8xl leading-none font-medium">
                    €{course.price}
                  </span>
                </div>
                <div className="text-white/50 text-sm mt-2">iva esclusa</div>

                <button
                  type="button"
                  onClick={() => {
                    trackCourseLead({ courseName: course.title });
                    navigate(withTrackingParams(`/corso/${canonicalSlug}/upsell`));
                  }}
                  className="btn-primary mt-8 w-full justify-center"
                >
                  Iscriviti al corso <ArrowUpRight size={18} />
                </button>

                <div className="mt-6 pt-6 border-t border-white/10 space-y-3 text-xs text-white/60">
                  <div className="flex items-center gap-2">
                    <ShieldCheck size={14} className="text-lime" style={{ color: "#d4ff3d" }} />
                    Garanzia Zero Rischi — rimborso entro le 13
                  </div>
                  <div className="flex items-center gap-2">
                    <Sparkles size={14} className="text-lime" style={{ color: "#d4ff3d" }} />
                    3 rate senza interessi disponibili con PayPal
                  </div>
                </div>
              </div>

              <p className="mt-4 text-white/40 text-xs text-center">
                Vuoi il percorso completo con tutti e 5 i corsi?{" "}
                <Link to="/#prezzo" className="text-white/70 hover:text-white underline">
                  Scopri l'All-In Experience a €1.143
                </Link>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FORMATORE (compatto) */}
      <section className="py-16 lg:py-24 bg-[#0c0c0c] border-y border-white/5">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-4">
              <div className="relative rounded-2xl overflow-hidden border border-white/10 aspect-[4/5] max-w-sm">
                <img
                  src="/francesco-formatore-bw-neon.jpg"
                  alt="Frank Gheri"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-black/20" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="font-mono-label text-white/60 text-[11px]">Docente del corso</div>
                  <div className="font-display text-2xl text-white mt-1">Frank Gheri</div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-8">
              <span className="section-label">Chi ti guiderà</span>
              <h2 className="font-display text-3xl lg:text-5xl text-white mt-6 leading-tight">
                Non un teorico.<br />
                <span className="italic font-light text-white/60">Un imprenditore digitale.</span>
              </h2>
              <p className="text-white/70 text-base lg:text-lg mt-6 leading-relaxed max-w-2xl">
                Francesco è Funnel Manager e founder di Digital Formula. Ogni giorno costruisce
                ecosistemi digitali per aziende reali. Non insegna cose che non ha già testato in
                agenzia. Zero fuffa, solo metodo applicato.
              </p>
              <Link to="/#formatore" className="btn-secondary mt-8 inline-flex">
                Scopri di più su Francesco <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS COMPATTI */}
      <section className="py-16 lg:py-24">
        <div className="container-max">
          <div className="mb-12">
            <span className="section-label">Testimonials</span>
            <h2 className="font-display text-3xl lg:text-5xl text-white mt-6 max-w-3xl leading-tight">
              Chi lo ha vissuto,<br />
              <span className="italic font-light text-white/60">lo racconta.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {TESTIMONIALS.slice(0, 2).map((t, i) => (
              <div key={i} className="bg-card border border-white/10 rounded-2xl p-7 hover-lift">
                <p className="text-white/85 text-base lg:text-lg leading-relaxed">"{t.text}"</p>
                <div className="mt-6 pt-5 border-t border-white/10">
                  <div className="font-display text-white text-base">{t.name}</div>
                  <div className="text-white/50 text-xs mt-1">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ COMPATTA */}
      <section className="py-16 lg:py-24 bg-[#0c0c0c] border-y border-white/5">
        <div className="container-max">
          <div className="mb-10">
            <span className="section-label">Domande frequenti</span>
            <h2 className="font-display text-3xl lg:text-5xl text-white mt-6 leading-tight">
              Hai altre domande?
            </h2>
          </div>
          <div className="space-y-2 max-w-3xl">
            {FAQS.slice(0, 5).map((f, i) => (
              <details key={i} className="group bg-card border border-white/10 rounded-xl">
                <summary className="cursor-pointer list-none flex items-center justify-between px-5 py-4 text-white text-base font-medium">
                  <span>{f.q}</span>
                  <span className="text-white/50 group-open:rotate-45 transition-transform">+</span>
                </summary>
                <div className="px-5 pb-5 text-white/70 text-sm leading-relaxed">{f.a}</div>
              </details>
            ))}
          </div>
          <Link to="/#faq" className="btn-secondary mt-8 inline-flex">
            Vedi tutte le FAQ <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>

      {/* CTA FINALE */}
      <section className="py-20 lg:py-32 text-center">
        <div className="container-max">
          <h2 className="font-display text-4xl lg:text-6xl text-white max-w-3xl mx-auto leading-tight">
            Iscriviti oggi.<br />
            <span className="italic font-light text-white/60">Il primo passo è tuo.</span>
          </h2>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a href="#acquista" className="btn-primary" onClick={() => trackCourseLead({ courseName: course.title })}>
              Iscriviti — €{course.price} <ArrowUpRight size={18} />
            </a>
            <button
              type="button"
              onClick={onOpenBooking}
              className="btn-secondary"
            >
              Parla con un ns consulente
            </button>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat courseName={course?.title?.split("—")[0].split("–")[0].trim()} />
    </div>
  );
}
